import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import type {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormGetValues,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useAppDispatch } from "@/stores/hook";
import { setIsAddCourse } from "../stores/courseFunctionalSlice";
// 1. Import the Course Mutation Hook
import { useCourseMutations } from "../api/useCourseMutations";
import useCurrentCourse from "../api/useCurrentCourse";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import { useState } from "react";
import useCourseBrochure, {
  courseBrochureQueryKey,
  type BrochureDoc,
} from "../api/useCourseBrochure";
import { useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { doc_endpoint } from "@/config/endpoints";
// CustomInputScopeSchema not needed here; scopes are validated by backend

const EligibilitySchema = z
  .object({
    qualification: z.string().optional().nullable(),
    value: z.union([z.string(), z.number()]).optional().nullable(),
  })
  .passthrough();

const positiveNumberString = (label: string) =>
  z
    .union([z.string(), z.number()])
    .refine(
      (v) => v !== undefined && v !== null && String(v).trim() !== "",
      { message: `${label} is required` }
    )
    .refine((v) => !Number.isNaN(Number(v)), {
      message: `${label} must be a valid number`,
    })
    .refine((v) => Number(v) > 0, {
      message: `${label} must be greater than 0`,
    });

/** Optional fee: blank is fine ("not specified"), otherwise a number ≥ 0. */
const optionalNonNegativeNumberString = (label: string) =>
  z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .refine(
      (v) =>
        v === undefined ||
        v === null ||
        String(v).trim() === "" ||
        (!Number.isNaN(Number(v)) && Number(v) >= 0),
      { message: `${label} must be a number (0 or more)` },
    );

const CourseSchema = z.object({
  name: z
    .string({ error: "Course name is required" })
    .trim()
    .min(1, "Course name is required"),
  /**
   * ADD mode: every university picked gets its own copy of the course.
   * EDIT mode: exactly one — the row being edited belongs to one university.
   */
  universityIds: z
    .array(z.string().min(1))
    .min(1, "University is required"),
  level: z
    .string({ error: "Program type is required" })
    .min(1, "Program type is required"),
  courseType: z.enum(["Online", "Campus", "Hybrid"], {
    error: "Course type is required",
  }),
  duration: positiveNumberString("Duration"),
  durationUnit: z.enum(["months", "years"]),
  minFee: optionalNonNegativeNumberString("Tuition fee"),

  description: z.string().optional(),
  qualification: z.string().optional(),

  customInputScopes: z.array(z.any()).optional(),

  eligibility: z.array(EligibilitySchema).optional(),
  country: z.string().optional(),
  programType: z.string().optional(),
  durationRange: z.string().optional(),
  courseTypeFilter: z.string().optional(),
});

type CourseFormData = z.infer<typeof CourseSchema>;

interface FormCourseContextType {
  register: UseFormRegister<CourseFormData>;
  control: Control<CourseFormData>;
  errors: FieldErrors<CourseFormData>;
  handleSubmit: UseFormHandleSubmit<CourseFormData>;
  watch: any;
  reset: () => void;
  onSubmit: (data: CourseFormData) => void;
  isSubmitting: boolean;
  setValue: UseFormSetValue<CourseFormData>;
  getValues: UseFormGetValues<CourseFormData>;
  isEditMode: boolean;
  clearErrors: any;
  setError: any;
  // Brochure handling (kept outside RHF so File survives validation)
  pendingBrochure: File | null;
  setPendingBrochure: (file: File | null) => void;
  existingBrochure: BrochureDoc | null;
  isBrochureUploading: boolean;
  uploadBrochureNow: (file: File) => Promise<void>;
  deleteBrochureNow: () => Promise<void>;
  isBrochureDeleting: boolean;
  courseId: number | null | undefined;
  /**
   * Optional drawer close handler — set when the form is rendered inside
   * the EditCourse sheet. Lets nested controls (e.g. delete) dismiss the
   * drawer without prop-drilling.
   */
  closeDrawer?: () => void;
}

const FormCourseContext = createContext<FormCourseContextType | undefined>(
  undefined
);

const FIELD_LABELS: Partial<Record<keyof CourseFormData, string>> = {
  name: "Course name",
  universityIds: "University",
  level: "Program type",
  courseType: "Course type",
  duration: "Duration",
  minFee: "Tuition fee",
  description: "Course overview",
  eligibility: "Eligibility",
};

const extractFirstMessage = (node: any): string | null => {
  if (!node) return null;
  if (typeof node?.message === "string" && node.message.trim()) {
    return node.message;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      const m = extractFirstMessage(item);
      if (m) return m;
    }
  } else if (typeof node === "object") {
    for (const key of Object.keys(node)) {
      const m = extractFirstMessage(node[key]);
      if (m) return m;
    }
  }
  return null;
};

const onInvalid = (errors: FieldErrors<CourseFormData>) => {
  const errorKeys = Object.keys(errors) as Array<keyof CourseFormData>;
  if (errorKeys.length === 0) return;

  const firstKey = errorKeys[0];
  const label = FIELD_LABELS[firstKey] ?? String(firstKey);
  const message =
    extractFirstMessage(errors[firstKey]) ?? `${label} is required`;
  toast.error(message);
};

function CourseFormProvider({
  children,
  courseId,
  closeDrawer,
}: {
  children: ReactNode;
  courseId?: number | null;
  closeDrawer?: () => void;
}) {
  const dispatch = useAppDispatch();
  const isEditMode = Boolean(courseId);

  const { course } = useCurrentCourse(courseId);

  const { createCourse, updateCourse } = useCourseMutations();
  const { uploadFile } = useDocumentMutaion();
  const queryClient = useQueryClient();
  const [isUploadingBrochure, setIsUploadingBrochure] = useState(false);
  const [isDeletingBrochure, setIsDeletingBrochure] = useState(false);
  const [pendingBrochure, setPendingBrochure] = useState<File | null>(null);

  const { brochure: existingBrochure } = useCourseBrochure(courseId);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<CourseFormData>({
    resolver: zodResolver(CourseSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      universityIds: [],
      description: "",
      duration: "6",
      durationUnit: "months",
      courseType: "Campus",
      customInputScopes: [],
      level: "",
      minFee: "",
      eligibility: [{ qualification: "", value: "" }],
    },
  });

  /* ---------------- EDIT MODE SYNC ---------------- */
  useEffect(() => {
    if (!isEditMode || !course) return;

    const totalMonths = Number(course.duration ?? 0);
    const useYears = totalMonths >= 12 && totalMonths % 12 === 0;
    const displayDuration = useYears
      ? String(totalMonths / 12)
      : String(totalMonths || "");

    reset({
      name: course.name ?? "",
      // 0 is "not specified" — show an empty box, not a literal 0.
      minFee: Number(course.minFee) > 0 ? String(course.minFee) : "",
      level: String(course.qualificationLevelId ?? ""),
      universityIds: [String(course.universityId)],
      description: course.description ?? "",
      duration: displayDuration,
      durationUnit: useYears ? "years" : "months",
      courseType: course.courseType,
      customInputScopes: course.customInputScopes ?? [],
      eligibility: course.eligibility ?? [{ qualification: "", value: "" }],
    });
  }, [course, isEditMode, reset]);

  /* ---------------- SUBMIT ---------------- */

  const deleteExistingBrochures = async (targetCourseId: number) => {
    try {
      const res = await axiosInstance.get(doc_endpoint, {
        params: {
          attachedTo: "course",
          attachedScope: targetCourseId,
          limit: 50,
        },
      });
      const docs: BrochureDoc[] = res.data?.data ?? [];
      const brochures = docs.filter(
        (d) => (d.category || "").toLowerCase() === "brochure"
      );
      await Promise.all(
        brochures.map((d) =>
          axiosInstance.delete(`${doc_endpoint}/${d.id}`).catch(() => null)
        )
      );
    } catch {
      // Non-fatal: continue with the upload even if cleanup fails.
    }
  };

  const uploadBrochure = async (file: File, targetCourseId: number) => {
    try {
      setIsUploadingBrochure(true);
      // Replace any existing brochure on this course first
      await deleteExistingBrochures(targetCourseId);
      await uploadFile.mutateAsync({
        file,
        attachedTo: "course",
        attachedScope: String(targetCourseId),
        category: "brochure",
        name: file.name,
      });
      await queryClient.invalidateQueries({
        queryKey: courseBrochureQueryKey(targetCourseId),
      });
    } finally {
      setIsUploadingBrochure(false);
    }
  };

  // Used by the brochure card to upload immediately when the user picks a
  // file in edit mode (matches the pattern used by ChildDocUpload, etc.).
  const uploadBrochureNow = async (file: File) => {
    if (!courseId) {
      // No course yet → defer until create() succeeds.
      setPendingBrochure(file);
      return;
    }
    await uploadBrochure(file, courseId);
  };

  const deleteBrochureNow = async () => {
    // Create mode → just clear the queued file.
    if (!courseId) {
      setPendingBrochure(null);
      return;
    }
    try {
      setIsDeletingBrochure(true);
      await deleteExistingBrochures(courseId);
      setPendingBrochure(null);
      await queryClient.invalidateQueries({
        queryKey: courseBrochureQueryKey(courseId),
      });
      toast.success("Brochure deleted");
    } catch {
      toast.error("Failed to delete brochure");
    } finally {
      setIsDeletingBrochure(false);
    }
  };

  const onSubmit = async (data: CourseFormData) => {
    const rawDuration = Number(data.duration);
    const durationInMonths =
      data.durationUnit === "years" ? rawDuration * 12 : rawDuration;

    // Strip UI-only fields before sending JSON. Brochure is tracked outside
    // RHF (in pendingBrochure) so the File survives reliably, and is uploaded
    // separately via /core/doc/upload once the course exists.
    const { durationUnit: _unit, universityIds, minFee, ...rest } = data;

    const cleanData = {
      ...rest,
      // Blank fee → omitted; the server stores 0 ("not specified").
      minFee:
        minFee == null || String(minFee).trim() === "" ? undefined : minFee,
      duration: String(durationInMonths),
      customInputScopes: (data.customInputScopes ?? []).filter(Boolean),
      // Single id for the edit PATCH (and for an older server on create);
      // the full list only rides the create, where the server fans out one
      // course per university inside a single transaction.
      universityId: universityIds[0],
      ...(isEditMode ? {} : { universityIds }),
    };

    try {
      if (isEditMode) {
        if (!courseId) return;
        await updateCourse.mutateAsync({ id: courseId, data: cleanData });
        if (pendingBrochure) {
          await uploadBrochure(pendingBrochure, courseId);
          setPendingBrochure(null);
        }
      } else {
        const res: any = await createCourse.mutateAsync(cleanData);
        // Every created row gets the brochure — `courses` lists them all;
        // fall back to the single `course` for an older server.
        const createdIds: number[] = (
          Array.isArray(res?.data?.courses) && res.data.courses.length > 0
            ? res.data.courses.map((c: any) => c?.id)
            : [res?.data?.course?.id ?? res?.course?.id ?? res?.data?.id]
        )
          .map((id: unknown) => Number(id))
          .filter((id: number) => Number.isFinite(id) && id > 0);
        if (pendingBrochure && createdIds.length > 0) {
          for (const id of createdIds) {
            await uploadBrochure(pendingBrochure, id);
          }
          setPendingBrochure(null);
        }
        dispatch(setIsAddCourse(false));
        reset();
      }
    } catch {
      // Errors already toasted by individual mutations.
    }
  };

  return (
    <FormCourseContext.Provider
      value={{
        register,
        control,
        errors,
        watch,
        setValue,
        getValues,
        reset,
        isEditMode,
        setError,
        clearErrors,
        handleSubmit: (fn) => handleSubmit(fn, onInvalid),
        onSubmit,
        isSubmitting:
          createCourse.isPending ||
          updateCourse.isPending ||
          isUploadingBrochure,
        pendingBrochure,
        setPendingBrochure,
        existingBrochure,
        isBrochureUploading: isUploadingBrochure,
        uploadBrochureNow,
        deleteBrochureNow,
        isBrochureDeleting: isDeletingBrochure,
        courseId,
        closeDrawer,
      }}
    >
      {children}
    </FormCourseContext.Provider>
  );
}

function useCourseForm(): FormCourseContextType {
  const context = useContext(FormCourseContext);
  if (!context) {
    throw new Error("useCourseForm must be used within CourseFormProvider");
  }
  return context;
}

export { CourseFormProvider, useCourseForm };
